import {useCallback, useState} from 'react';
import {
  isEmail,
  useContactVerification,
  useCustomer,
} from '@resurs-internal/rb-app-api';
import {useTranslate} from '../../hooks';
import type {
  ContactChannel,
  VerifyContactMode,
  VerifyContactStep,
} from './VerifyContactDialogView';
import {isValidPhone} from './utils/phoneE164';

export const useVerifyContactFlow = ({
  channel,
  onVerified,
  onClose,
}: {
  channel: ContactChannel;
  onVerified: (verifiedValue: string) => void | Promise<void>;
  onClose: () => void;
}) => {
  const verification = useContactVerification();
  const customer = useCustomer();
  const translate = useTranslate();

  const data = customer.query.data;
  const currentValue = getCurrentValue(channel, data);
  const isVerified = getIsVerified(channel, data);
  const mode = getMode(currentValue, isVerified);

  const [step, setStep] = useState<VerifyContactStep>('input');
  const [code, setCode] = useState<string[]>([]);
  const [value, setValue] = useState<string>(currentValue);

  const isValueValid = validateValue(channel, value);
  const isValueDifferent = mode !== 'edit' || value.trim() !== currentValue;
  const isCodeComplete = code.filter(Boolean).length === 6;
  const isBusy = verification.isVerifying;

  const sendCode = useCallback(async () => {
    if (!isValueValid || !isValueDifferent) return;
    try {
      await verification.sendOtp(channel, value.trim());
      setStep('code');
    } catch {
      // Error surfaces via verification.sendError
    }
  }, [channel, isValueValid, isValueDifferent, value, verification]);

  const verify = useCallback(
    async (otp?: string) => {
      try {
        await verification.verifyOtp.mutateAsync(otp ?? code.join(''));
        setStep('success');
      } catch {
        setCode([]);
      }
    },
    [code, verification.verifyOtp],
  );

  const handleDone = useCallback(async () => {
    await onVerified(value.trim());
    onClose();
  }, [onVerified, onClose, value]);

  const handleClose = step === 'success' ? handleDone : onClose;

  const buildPrimaryAction = () => {
    if (step === 'success') {
      return {
        title: translate('verify-contact.done'),
        color: 'primary' as const,
        onClick: handleDone,
      };
    }
    if (step === 'code') {
      return {
        title: translate('click.to.pay.verify'),
        color: 'primary' as const,
        onClick: () => verify(),
        disabled: !isCodeComplete || isBusy,
      };
    }
    return {
      title: translate('verify-contact.send-code'),
      color: 'primary' as const,
      onClick: sendCode,
      disabled: !isValueValid || !isValueDifferent || isBusy,
    };
  };

  return {
    channel,
    step,
    mode,
    value,
    setValue,
    displayValue: value.trim() || value,
    isValueValid,
    code,
    setCode,
    isCodeComplete,
    isBusy,
    sendError: verification.sendError,
    verifyError: verification.verifyError,
    sendCode,
    verify,
    handleClose,
    primaryAction: buildPrimaryAction(),
  };
};

export type VerifyContactFlow = ReturnType<typeof useVerifyContactFlow>;

interface CustomerContactFields {
  email?: string;
  emailVerified?: boolean;
  mobilePhoneNo?: string;
  mobilePhoneNoVerified?: boolean;
}

const getCurrentValue = (
  channel: ContactChannel,
  data: CustomerContactFields | undefined,
) => {
  if (channel === 'email') return data?.email ?? '';
  return data?.mobilePhoneNo ?? '';
};

const getIsVerified = (
  channel: ContactChannel,
  data: CustomerContactFields | undefined,
) => {
  if (channel === 'email') return !!data?.emailVerified;
  return !!data?.mobilePhoneNoVerified;
};

const getMode = (
  currentValue: string,
  isVerified: boolean,
): VerifyContactMode => {
  if (!currentValue) return 'add';
  if (isVerified) return 'edit';
  return 'verify';
};

const validateValue = (channel: ContactChannel, value: string) =>
  channel === 'email' ? isEmail(value.trim()) : isValidPhone(value);
